import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { movimientosAPI, proveedoresAPI, pagosAPI, movimientosPredefinidosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';
import '../Movimientos.css';
import { sortMovimientosCronologico, deltaSaldoMovimiento } from '../utils/movimientosOrden';
import {
  imprimirComprobanteMovimiento,
  imprimirReporteMovimientos,
  movimientoTienePago,
} from '../utils/impresionMovimientos';
import { useMovimientoDetalle } from '../hooks/useMovimientoDetalle';
import { MovimientoDetalleModal } from '../components/MovimientoDetalleModal';
import { MovimientoConceptoCell } from '../components/MovimientoConceptoCell';
import FiltroPredefinidosMultiselect from '../components/FiltroPredefinidosMultiselect';
import { ExportActions } from '../components/ExportActions';
import {
  exportFromColumnas,
  valorCeldaMovimientoExport,
  COLUMNAS_MOVIMIENTOS_CUENTA,
} from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const getSortValue = (mov, colId) => {
  switch (colId) {
    case 'fecha':
      return new Date(mov.fecha || 0).getTime() || 0;
    case 'concepto':
      return (mov.concepto || '').toString().toLowerCase();
    case 'debito':
      return mov.tipo_movimiento === 'DEBITO' ? (parseFloat(mov.monto) || 0) : 0;
    case 'credito':
      return mov.tipo_movimiento === 'CREDITO' ? (parseFloat(mov.monto) || 0) : 0;
    case 'saldo':
      return parseFloat(mov.saldo_acumulado || 0) || 0;
    default:
      return '';
  }
};

const ProveedorMovimientos = () => {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const [proveedor, setProveedor] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const primeraCargaMovRef = useRef(true);
  const [refrescando, setRefrescando] = useState(false);
  const cargaMovSeqRef = useRef(0);
  const [filtros, setFiltros] = useState(() =>
    loadFiltrosSession(
      id ? `proveedor_${id}_movimientos_filtros` : 'proveedor_movimientos_filtros',
      { fecha_desde: '', fecha_hasta: '', movimiento_predefinido_ids: [] }
    )
  );
  const fechaDefaultAplicadaRef = useRef(false);
  const [predefinidos, setPredefinidos] = useState([]);
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [saldoFiltrado, setSaldoFiltrado] = useState(null);
  const [saldoCuenta, setSaldoCuenta] = useState(null);
  const [saldoInicial, setSaldoInicial] = useState(null);
  const { formatFecha, formatFechaHora, fechaHoy } = useZonaHoraria();
  const { logoUrl } = useBranding();

  useEffect(() => {
    if (!id) return;
    setFiltros(
      loadFiltrosSession(`proveedor_${id}_movimientos_filtros`, {
        fecha_desde: '',
        fecha_hasta: '',
        movimiento_predefinido_ids: [],
      })
    );
    fechaDefaultAplicadaRef.current = false;
    primeraCargaMovRef.current = true;
  }, [id]);

  useEffect(() => {
    if (!id) return;
    saveFiltrosSession(`proveedor_${id}_movimientos_filtros`, filtros);
  }, [id, filtros]);

  useEffect(() => {
    if (fechaDefaultAplicadaRef.current) return;
    fechaDefaultAplicadaRef.current = true;
    setFiltros((prev) => (prev.fecha_desde ? prev : { ...prev, fecha_desde: fechaHoy() }));
  }, [fechaHoy]);
  const {
    detalleMovimiento,
    detalleRecibo,
    detallePago,
    detallePagoError,
    detalleLoading,
    abrirDetalle,
    cerrarDetalle,
  } = useMovimientoDetalle();
  const [anulandoComprobante, setAnulandoComprobante] = useState(false);

  const loadProveedor = useCallback(async () => {
    if (!id) return;
    try {
      const response = await proveedoresAPI.getById(id);
      if (response.success) setProveedor(response.data);
    } catch {
      toast.error('Error al cargar proveedor');
    }
  }, [id]);

  const loadPredefinidos = useCallback(async () => {
    try {
      const res = await movimientosPredefinidosAPI.getAll({ activo: true, uso: 'proveedores', para_filtro: '1' });
      if (res.success && Array.isArray(res.data)) setPredefinidos(res.data);
    } catch {
      setPredefinidos([]);
    }
  }, []);

  useEffect(() => { loadPredefinidos(); }, [loadPredefinidos]);

  const loadMovimientos = useCallback(async () => {
    if (!id) return;
    const mostrarLoaderPantalla = primeraCargaMovRef.current;
    const seq = ++cargaMovSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescando(true);
      const params = {};
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      if (filtros.movimiento_predefinido_ids?.length) {
        params.movimiento_predefinido_ids = filtros.movimiento_predefinido_ids.join(',');
      }
      if (filtros.fecha_desde || filtros.fecha_hasta || filtros.movimiento_predefinido_ids?.length) {
        params.incluir_resumen_saldos = '1';
      }
      const response = await movimientosAPI.getByProveedor(id, params);
      if (seq !== cargaMovSeqRef.current) return;
      if (response.success) {
        setMovimientos(Array.isArray(response.data) ? response.data : []);
        setSaldoFiltrado(response.saldo_filtrado !== undefined ? response.saldo_filtrado : null);
        setSaldoCuenta(response.saldo_cuenta !== undefined ? response.saldo_cuenta : null);
        setSaldoInicial(response.saldo_inicial !== undefined ? response.saldo_inicial : null);
      } else {
        setMovimientos([]);
        setSaldoFiltrado(null);
        setSaldoCuenta(null);
        setSaldoInicial(null);
      }
    } catch (error) {
      if (seq !== cargaMovSeqRef.current) return;
      setMovimientos([]);
      setSaldoFiltrado(null);
      setSaldoCuenta(null);
      setSaldoInicial(null);
      toast.error(error.message || 'Error al cargar movimientos');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaMovRef.current = false;
      }
      if (seq === cargaMovSeqRef.current) setRefrescando(false);
    }
  }, [id, filtros.fecha_desde, filtros.fecha_hasta, filtros.movimiento_predefinido_ids]);

  useEffect(() => { loadProveedor(); }, [loadProveedor]);
  useEffect(() => { loadMovimientos(); }, [loadMovimientos]);

  const formatMoneda = (n) =>
    parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const hayFiltrosActivos = !!(filtros.fecha_desde || filtros.fecha_hasta || filtros.movimiento_predefinido_ids?.length);

  const saldoCuentaReal = useMemo(() => {
    if (saldoCuenta !== null) return saldoCuenta;
    const porFecha = [...movimientos].sort(sortMovimientosCronologico);
    let saldo = 0;
    porFecha.forEach((m) => { saldo += deltaSaldoMovimiento(m); });
    return saldo;
  }, [movimientos, saldoCuenta]);

  const movimientosOrdenados = useMemo(() => {
    if (!sortByColumn) return [...movimientos].sort(sortMovimientosCronologico);
    if (sortByColumn === 'saldo') {
      const porFecha = [...movimientos].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
      let s = 0;
      const conSaldo = porFecha.map(m => {
        s += m.tipo_movimiento === 'DEBITO' ? (parseFloat(m.monto) || 0) : -(parseFloat(m.monto) || 0);
        return { ...m, _saldoParaOrdenar: s };
      });
      conSaldo.sort((a, b) => {
        const v = (a._saldoParaOrdenar || 0) - (b._saldoParaOrdenar || 0);
        return sortAsc ? v : -v;
      });
      return conSaldo;
    }
    const arr = [...movimientos];
    arr.sort((a, b) => {
      const v1 = getSortValue(a, sortByColumn);
      const v2 = getSortValue(b, sortByColumn);
      const num = typeof v1 === 'number' && typeof v2 === 'number';
      let cmp = 0;
      if (num) cmp = v1 - v2;
      else cmp = String(v1).localeCompare(String(v2), 'es');
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [movimientos, sortByColumn, sortAsc]);

  const movimientosParaMostrar = useMemo(() => {
    const base = saldoInicial != null ? Number(saldoInicial) : 0;
    const porFecha = [...movimientos].sort(sortMovimientosCronologico);
    const saldoPorId = {};
    let saldo = base;
    porFecha.forEach((m) => {
      saldo += deltaSaldoMovimiento(m);
      saldoPorId[m.id] = saldo;
    });
    return movimientosOrdenados.map((m) => ({ ...m, saldo_acumulado: saldoPorId[m.id] ?? base }));
  }, [movimientosOrdenados, movimientos, saldoInicial]);

  const saldoCierreReporte = useMemo(() => {
    if (!movimientos.length) return saldoCuentaReal ?? 0;
    const base = saldoInicial != null ? Number(saldoInicial) : 0;
    return movimientos.reduce((acc, m) => acc + deltaSaldoMovimiento(m), base);
  }, [movimientos, saldoInicial, saldoCuentaReal]);

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) setSortAsc(prev => !prev);
    else { setSortByColumn(columnId); setSortAsc(true); }
  };

  const generarPDF = () => {
    const fechaReporte = new Date();
    const fechaReporteStr = `${fechaReporte.getDate()} de ${MESES_ES[fechaReporte.getMonth()]} de ${fechaReporte.getFullYear()}`;
    const periodoHtml =
      filtros.fecha_desde || filtros.fecha_hasta
        ? `<p><strong>Período:</strong> ${filtros.fecha_desde || '...'} a ${filtros.fecha_hasta || '...'}</p>`
        : '';

    imprimirReporteMovimientos({
      titulo: 'Movimientos de proveedor',
      subtitulo: `Reporte generado el ${fechaReporteStr}`,
      entidadInfoHtml: `
        <p><strong>Proveedor:</strong> ${(proveedor?.razon_social || '').replace(/</g, '&lt;')}</p>
        <p><strong>CUIT:</strong> ${proveedor?.cuit || '-'}</p>
        ${proveedor?.tipo_nombre ? `<p><strong>Tipo:</strong> ${proveedor.tipo_nombre}</p>` : ''}
      `,
      periodoHtml,
      movimientos: movimientosParaMostrar,
      formatFecha,
      saldoFinal: saldoCierreReporte,
      logoUrl,
    });
  };

  const handleExportListado = async (format) => {
    try {
      const nombre = proveedor?.razon_social ? `movimientos_proveedor_${proveedor.razon_social}` : 'movimientos_proveedor';
      await exportFromColumnas({
        filename: nombre,
        sheetName: 'Movimientos',
        columnDefs: COLUMNAS_MOVIMIENTOS_CUENTA,
        visibleColumnIds: COLUMNAS_MOVIMIENTOS_CUENTA.map((c) => c.id),
        items: movimientosParaMostrar,
        getCellValue: (mov, colId) => valorCeldaMovimientoExport(mov, colId, formatFecha),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const handleImprimirFila = async (mov) => {
    try {
      const ok = await imprimirComprobanteMovimiento(mov, formatFecha, logoUrl);
      if (!ok) toast.error('No se pudo imprimir el comprobante');
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  const handleAnularPago = async () => {
    if (!detallePago?.id || detallePago.anulado_at) return;
    if (!window.confirm(`¿Anular la Orden de Pago N° ${detallePago.numero_pago}? Esta acción revierte el movimiento y los fondos asociados.`)) return;
    setAnulandoComprobante(true);
    try {
      const res = await pagosAPI.anular(detallePago.id);
      if (res.success) {
        toast.success(res.message || 'Orden de pago anulada');
        cerrarDetalle();
        loadMovimientos();
      } else {
        toast.error(res.message || 'No se pudo anular');
      }
    } catch (error) {
      toast.error(error.message || 'Error al anular orden de pago');
    } finally {
      setAnulandoComprobante(false);
    }
  };

  return (
    <div className="movimientos pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Movimientos del proveedor</h1>
        <div className="page-header-acciones">
          <Link to="/proveedores" className="btn-secondary">← Volver a proveedores</Link>
          {hasPermission('ver_movimientos') && movimientos.length > 0 && (
            <ExportActions
              onCsv={() => handleExportListado('csv')}
              onExcel={() => handleExportListado('excel')}
              onPrint={generarPDF}
              printLabel="Generar reporte PDF"
            />
          )}
        </div>
      </div>

      <div className="pagina-listado-fijo">
      {proveedor && (
        <div style={{
          padding: '14px',
          background: '#f0f4f8',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #ddd'
        }}>
          <strong>{proveedor.razon_social}</strong>
          {proveedor.cuit && <span style={{ marginLeft: '12px', color: '#666' }}>CUIT: {proveedor.cuit}</span>}
          {proveedor.tipo_nombre && (
            <span style={{ marginLeft: '12px', padding: '2px 8px', background: '#e8f4f8', borderRadius: '4px', fontSize: '12px' }}>
              {proveedor.tipo_nombre}
            </span>
          )}
          {!hayFiltrosActivos && (
            <span style={{ marginLeft: '12px', fontWeight: 'bold' }}>
              Saldo de la cuenta: ${formatMoneda(saldoCuentaReal)}
            </span>
          )}
        </div>
      )}

      {hayFiltrosActivos && (saldoFiltrado !== null || saldoCuenta !== null || saldoInicial !== null) && (
        <div style={{ marginBottom: 16, padding: 12, background: '#f0f4f8', borderRadius: 8, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {saldoFiltrado !== null && (
            <div>
              <strong>Saldo de los movimientos filtrados</strong>:{' '}
              <span className={saldoFiltrado > 0 ? 'negative' : saldoFiltrado < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoFiltrado)}
              </span>
            </div>
          )}
          {saldoCuenta !== null && (
            <div>
              <strong>Saldo acumulado de la cuenta (sin filtros)</strong>:{' '}
              <span className={saldoCuenta > 0 ? 'negative' : saldoCuenta < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoCuenta)}
              </span>
            </div>
          )}
          {saldoInicial !== null && (
            <div>
              <strong>Saldo inicial</strong> {filtros.fecha_desde && `(al ${formatFecha(filtros.fecha_desde)}): `}
              <span className={saldoInicial > 0 ? 'negative' : saldoInicial < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoInicial)}
              </span>
            </div>
          )}
        </div>
      )}
      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center' }}>
          <input
            type="date"
            name="fecha_desde"
            value={filtros.fecha_desde}
            onChange={e => setFiltros(prev => ({ ...prev, fecha_desde: e.target.value }))}
            placeholder="Fecha desde"
          />
          <input
            type="date"
            name="fecha_hasta"
            value={filtros.fecha_hasta}
            onChange={e => setFiltros(prev => ({ ...prev, fecha_hasta: e.target.value }))}
            placeholder="Fecha hasta"
          />
          <FiltroPredefinidosMultiselect
            predefinidos={predefinidos}
            selectedIds={filtros.movimiento_predefinido_ids}
            onChange={(ids) => setFiltros((prev) => ({ ...prev, movimiento_predefinido_ids: ids }))}
            label="Tipo de movimiento"
          />
          {proveedor && (
            <div className="action-buttons" style={{ marginLeft: 4, flexShrink: 0 }}>
              {hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{ modoProveedores: true, proveedorId: proveedor.id }}
                  className="btn-action-icon"
                  title="Nuevo movimiento (débito)"
                >
                  <span aria-hidden="true">💵</span>
                </Link>
              )}
              {hasPermission('crear_creditos') && hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{ nuevoPago: true, modoProveedores: true, proveedorId: proveedor.id }}
                  className="btn-action-icon"
                  title="Nueva orden de pago"
                >
                  <span aria-hidden="true">💸</span>
                </Link>
              )}
              {hasPermission('editar_clientes') && (
                <Link
                  to={`/proveedores/editar/${proveedor.id}`}
                  className="btn-action-icon btn-action-edit"
                  title="Editar proveedor"
                >
                  <span aria-hidden="true">✏️</span>
                </Link>
              )}
            </div>
          )}
        </div>

      <div className="listado-panel-cuerpo clientes-listado-cuerpo">
      <div className={`listado-tabla-area clientes-listado-tabla-area${refrescando ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {[
                { id: 'fecha', label: 'Fecha' },
                { id: 'concepto', label: 'Concepto' },
                { id: 'debito', label: 'Débito' },
                { id: 'credito', label: 'Crédito' },
                { id: 'saldo', label: 'Saldo acumulado' },
              ].map(col => (
                <th
                  key={col.id}
                  className="th-sortable"
                  onClick={() => handleSortColumn(col.id)}
                  title={`Ordenar por ${col.label}. Clic de nuevo invierte el orden.`}
                >
                  <span>{col.label}</span>
                  {sortByColumn === col.id && (
                    <span className="sort-indicator" aria-hidden="true">
                      {sortAsc ? ' ▲' : ' ▼'}
                    </span>
                  )}
                </th>
              ))}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="no-data">Cargando...</td></tr>
            ) : movimientosParaMostrar.length === 0 ? (
              <tr><td colSpan="6" className="no-data">No hay movimientos en el período</td></tr>
            ) : (
              movimientosParaMostrar.map(mov => (
                <tr key={mov.id}>
                  <td>{formatFecha(mov.fecha)}</td>
                  <td>
                    <MovimientoConceptoCell mov={mov} />
                  </td>
                  <td className="negative">
                    {mov.tipo_movimiento === 'DEBITO'
                      ? <strong>${formatMoneda(mov.monto)}</strong>
                      : '-'}
                  </td>
                  <td className="positive">
                    {mov.tipo_movimiento === 'CREDITO'
                      ? <strong>${formatMoneda(mov.monto)}</strong>
                      : '-'}
                  </td>
                  <td
                    className={
                      parseFloat(mov.saldo_acumulado || 0) > 0
                        ? 'negative'
                        : parseFloat(mov.saldo_acumulado || 0) < 0
                          ? 'positive'
                          : ''
                    }
                  >
                    <strong>${formatMoneda(mov.saldo_acumulado)}</strong>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn-action-icon"
                        title="Ver detalle del movimiento"
                        onClick={() => abrirDetalle(mov)}
                      >
                        <span aria-hidden="true">🔍</span>
                      </button>
                      {hasPermission('ver_movimientos') && (
                        <button
                          type="button"
                          className="btn-action-icon"
                          title={movimientoTienePago(mov) ? 'Imprimir orden de pago' : 'Imprimir comprobante'}
                          onClick={() => handleImprimirFila(mov)}
                        >
                          <span aria-hidden="true">🖨️</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {refrescando && (
        <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
          <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
          <span className="clientes-listado-refresh-label">Actualizando…</span>
        </div>
      )}
      </div>
      </div>
      </div>

      <MovimientoDetalleModal
        mov={detalleMovimiento}
        recibo={detalleRecibo}
        pago={detallePago}
        pagoError={detallePagoError}
        loading={detalleLoading}
        esProveedores
        onClose={cerrarDetalle}
        formatFecha={formatFecha}
        formatFechaHora={formatFechaHora}
        hasPermission={hasPermission}
        onAnularPago={handleAnularPago}
        anulandoComprobante={anulandoComprobante}
        logoUrl={logoUrl}
      />
    </div>
  );
};

export default ProveedorMovimientos;
